/**
 * Firebase Notification Test Component
 *
 * This component provides a comprehensive testing interface for Firebase notifications.
 * It helps identify issues and test all notification functionality.
 */

import React, { useState, useEffect } from "react";
import { useFirebaseNotifications } from "../hooks/useFirebaseNotifications";

const FirebaseNotificationTester = () => {
  const [testResults, setTestResults] = useState([]);
  const [isTestingPermission, setIsTestingPermission] = useState(false);
  const [isTestingToken, setIsTestingToken] = useState(false);
  const [isTestingNotification, setIsTestingNotification] = useState(false);

  const {
    token,
    permission,
    isInitialized,
    error,
    requestPermission,
    initialize,
  } = useFirebaseNotifications();

  const addTestResult = (test, status, message, details = null) => {
    const result = {
      id: Date.now(),
      test,
      status, // 'success', 'warning', 'error'
      message,
      details,
      timestamp: new Date().toLocaleTimeString(),
    };
    setTestResults((prev) => [result, ...prev]);
  };

  // Auto-run basic checks on component mount
  useEffect(() => {
    runBasicChecks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runBasicChecks = () => {
    // Clear previous results
    setTestResults([]);

    // 1. Check notification support
    if (!("Notification" in window)) {
      addTestResult(
        "Browser Support",
        "error",
        "Notifications not supported in this browser"
      );
      return;
    }
    addTestResult("Browser Support", "success", "Notifications are supported");

    // 2. Check service worker support
    if (!("serviceWorker" in navigator)) {
      addTestResult(
        "Service Worker Support",
        "error",
        "Service workers not supported"
      );
      return;
    }
    addTestResult(
      "Service Worker Support",
      "success",
      "Service workers are supported"
    );

    // 3. Check current permission
    const currentPermission = Notification.permission;
    const permissionStatus =
      currentPermission === "granted"
        ? "success"
        : currentPermission === "denied"
        ? "error"
        : "warning";
    addTestResult(
      "Permission Status",
      permissionStatus,
      `Current permission: ${currentPermission}`
    );

    // 4. Check Firebase initialization
    if (isInitialized) {
      addTestResult(
        "Firebase Init",
        "success",
        "Firebase notifications initialized"
      );
    } else if (error) {
      addTestResult(
        "Firebase Init",
        "error",
        "Firebase initialization failed",
        error
      );
    } else {
      addTestResult("Firebase Init", "warning", "Firebase not initialized yet");
    }

    // 5. Check FCM token
    if (token) {
      addTestResult(
        "FCM Token",
        "success",
        `Token obtained (${token.substring(0, 20)}...)`
      );
    } else {
      addTestResult("FCM Token", "warning", "No FCM token available");
    }

    // 6. Check service worker registration
    checkServiceWorkerRegistration();
  };

  const checkServiceWorkerRegistration = async () => {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      const firebaseSwFound = registrations.find(
        (reg) =>
          reg.scope.includes("firebase-messaging-sw.js") ||
          reg.active?.scriptURL.includes("firebase-messaging-sw.js")
      );

      if (firebaseSwFound) {
        addTestResult(
          "Service Worker",
          "success",
          "Firebase messaging SW registered"
        );
      } else {
        addTestResult(
          "Service Worker",
          "error",
          "Firebase messaging SW not found"
        );
      }
    } catch (error) {
      addTestResult(
        "Service Worker",
        "error",
        "Error checking service worker",
        error.message
      );
    }
  };

  const testPermissionRequest = async () => {
    setIsTestingPermission(true);
    try {
      const granted = await requestPermission();
      if (granted) {
        addTestResult(
          "Permission Test",
          "success",
          "Permission granted successfully"
        );
      } else {
        addTestResult("Permission Test", "error", "Permission was denied");
      }
    } catch (error) {
      addTestResult(
        "Permission Test",
        "error",
        "Error requesting permission",
        error.message
      );
    } finally {
      setIsTestingPermission(false);
    }
  };

  const testTokenGeneration = async () => {
    setIsTestingToken(true);
    try {
      const newToken = await initialize();
      if (newToken) {
        addTestResult(
          "Token Test",
          "success",
          `New token generated: ${newToken.substring(0, 20)}...`
        );
      } else {
        addTestResult("Token Test", "error", "Failed to generate token");
      }
    } catch (error) {
      addTestResult(
        "Token Test",
        "error",
        "Error generating token",
        error.message
      );
    } finally {
      setIsTestingToken(false);
    }
  };

  const testBasicNotification = async () => {
    if (Notification.permission !== "granted") {
      addTestResult("Notification Test", "error", "Permission not granted");
      return;
    }

    setIsTestingNotification(true);
    try {
      const notification = new Notification("Test Notification", {
        body: "This is a test notification from VibeLinks",
        icon: "/logo192.png",
        badge: "/logo192.png",
        tag: "test-notification",
      });

      notification.onclick = () => {
        addTestResult(
          "Notification Test",
          "success",
          "Notification clicked by user"
        );
        notification.close();
      };

      setTimeout(() => {
        notification.close();
      }, 5000);

      addTestResult("Notification Test", "success", "Test notification sent");
    } catch (error) {
      addTestResult(
        "Notification Test",
        "error",
        "Error sending notification",
        error.message
      );
    } finally {
      setIsTestingNotification(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "success":
        return "✅";
      case "warning":
        return "⚠️";
      case "error":
        return "❌";
      default:
        return "ℹ️";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "success":
        return "text-green-600";
      case "warning":
        return "text-yellow-600";
      case "error":
        return "text-red-600";
      default:
        return "text-blue-600";
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">
        🔧 Firebase Notification Tester
      </h2>

      {/* Current Status */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-semibold mb-3">Current Status</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium">Permission:</span>
            <span
              className={`ml-2 ${
                permission === "granted"
                  ? "text-green-600"
                  : permission === "denied"
                  ? "text-red-600"
                  : "text-yellow-600"
              }`}
            >
              {permission || "Unknown"}
            </span>
          </div>
          <div>
            <span className="font-medium">Firebase Init:</span>
            <span
              className={`ml-2 ${
                isInitialized ? "text-green-600" : "text-yellow-600"
              }`}
            >
              {isInitialized ? "Yes" : "No"}
            </span>
          </div>
          <div>
            <span className="font-medium">FCM Token:</span>
            <span
              className={`ml-2 ${token ? "text-green-600" : "text-red-600"}`}
            >
              {token ? "Available" : "None"}
            </span>
          </div>
          <div>
            <span className="font-medium">Error:</span>
            <span
              className={`ml-2 ${error ? "text-red-600" : "text-green-600"}`}
            >
              {error || "None"}
            </span>
          </div>
        </div>
      </div>

      {/* Test Buttons */}
      <div className="mb-6 flex flex-wrap gap-3">
        <button
          onClick={runBasicChecks}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
        >
          🔄 Run Basic Checks
        </button>

        <button
          onClick={testPermissionRequest}
          disabled={isTestingPermission}
          className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-gray-400 transition-colors"
        >
          {isTestingPermission ? "⏳ Testing..." : "🔔 Test Permission"}
        </button>

        <button
          onClick={testTokenGeneration}
          disabled={isTestingToken}
          className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 disabled:bg-gray-400 transition-colors"
        >
          {isTestingToken ? "⏳ Testing..." : "🎫 Test Token"}
        </button>

        <button
          onClick={testBasicNotification}
          disabled={isTestingNotification || permission !== "granted"}
          className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 disabled:bg-gray-400 transition-colors"
        >
          {isTestingNotification ? "⏳ Testing..." : "📢 Test Notification"}
        </button>
      </div>

      {/* Test Results */}
      <div>
        <h3 className="text-lg font-semibold mb-3">Test Results</h3>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {testResults.length === 0 ? (
            <p className="text-gray-500 italic">
              No test results yet. Click "Run Basic Checks" to start.
            </p>
          ) : (
            testResults.map((result) => (
              <div
                key={result.id}
                className={`p-3 rounded border-l-4 ${
                  result.status === "success"
                    ? "border-green-500 bg-green-50"
                    : result.status === "warning"
                    ? "border-yellow-500 bg-yellow-50"
                    : result.status === "error"
                    ? "border-red-500 bg-red-50"
                    : "border-blue-500 bg-blue-50"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span>{getStatusIcon(result.status)}</span>
                      <span className="font-medium">{result.test}</span>
                      <span className="text-xs text-gray-500">
                        {result.timestamp}
                      </span>
                    </div>
                    <p className={`mt-1 ${getStatusColor(result.status)}`}>
                      {result.message}
                    </p>
                    {result.details && (
                      <details className="mt-2">
                        <summary className="text-xs text-gray-600 cursor-pointer">
                          Show details
                        </summary>
                        <pre className="text-xs bg-gray-100 p-2 mt-1 rounded overflow-auto">
                          {typeof result.details === "string"
                            ? result.details
                            : JSON.stringify(result.details, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <h4 className="font-semibold text-blue-800 mb-2">
          💡 Troubleshooting Tips
        </h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Make sure you're not in incognito/private browsing mode</li>
          <li>
            • Check that notifications are enabled in your browser settings
          </li>
          <li>
            • Ensure the service worker file exists at /firebase-messaging-sw.js
          </li>
          <li>
            • Permission requests must be triggered by user interaction (button
            click)
          </li>
          <li>• Check browser console for detailed error messages</li>
          <li>• Try refreshing the page after granting permissions</li>
        </ul>
      </div>
    </div>
  );
};

export default FirebaseNotificationTester;
